import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import React from "react";

import { monospaceStyle } from "../../theme";
import { formatFilterCount } from "./FilterRow";

// Height (px) a column's values take before they scroll
export const FILTER_COLUMN_HEIGHT = 232;

export interface FilterColumnProps {
  // The filter's name, shown in upper case
  label: string;
  // A number shown in monospace beside the name, e.g. the values it holds
  count?: number;
  // How many of its values are picked (or ranges set); the column is marked
  // while it holds any
  active?: number;
  // A quiet line under the values
  note?: string;
  children: React.ReactNode;
}

// What the mark of a column that holds picks says
export const pickedText = (active: number): string => `${active} picked`;

// One filter of a filter panel laid out in a row: a card of its own, with a
// head that names it over the values, which scroll on their own once they
// outgrow the card. It is the section of the side panel (see FilterSection)
// without the fold: side by side every filter is in view at once, so each
// is set apart by its own surface and border, and one that narrows the
// result is marked.
const FilterColumn: React.FC<FilterColumnProps> = ({
  label,
  count,
  active = 0,
  note,
  children,
}) => (
  <Box
    component="section"
    aria-label={label}
    data-active={active > 0}
    sx={{
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      border: 1,
      borderColor: (theme) =>
        active > 0
          ? alpha(theme.palette.accent.main, 0.55)
          : theme.palette.divider,
      borderRadius: (theme) => `${theme.radius.surface}px`,
      backgroundColor: "background.paper",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        px: 1.5,
        py: 1,
        borderBottom: 1,
        borderColor: "divider",
        color: active > 0 ? "text.primary" : "text.secondary",
      }}
    >
      <Typography
        component="h3"
        variant="caption"
        sx={{
          m: 0,
          minWidth: 0,
          fontSize: "0.72rem",
          lineHeight: 1.5,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          fontWeight: 600,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}
      >
        {active > 0 && (
          <Box
            component="span"
            aria-label={pickedText(active)}
            title={pickedText(active)}
            sx={{
              ...monospaceStyle,
              minWidth: 18,
              px: "5px",
              boxSizing: "border-box",
              borderRadius: "9px",
              fontSize: 11,
              lineHeight: "18px",
              textAlign: "center",
              color: "link.main",
              backgroundColor: (theme) => alpha(theme.palette.accent.main, 0.2),
            }}
          >
            {active}
          </Box>
        )}
        {count !== undefined && (
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            sx={monospaceStyle}
          >
            {formatFilterCount(count)}
          </Typography>
        )}
      </Box>
    </Box>
    <Box
      sx={{
        maxHeight: FILTER_COLUMN_HEIGHT,
        overflowY: "auto",
        px: 1,
        py: 0.75,
      }}
    >
      {children}
      {note && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", px: 0.5, pt: 0.5, lineHeight: 1.35 }}
        >
          {note}
        </Typography>
      )}
    </Box>
  </Box>
);

export default FilterColumn;
